/**
 * Lee un archivo como data URL sin procesar.
 */
export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Sin archivo"));
      return;
    }
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer"));
    r.readAsDataURL(file);
  });
}

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No se pudo cargar la imagen"));
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer blob"));
    r.readAsDataURL(blob);
  });
}

/**
 * Prueba calidades decrecientes hasta que el blob cumpla maxBytes (o se agote el mínimo).
 */
async function blobFromCanvasUnderLimit(getCanvas, mime, startQ, minQ, step, maxBytes) {
  let q = startQ;
  let best = null;
  while (q >= minQ) {
    const canvas = getCanvas();
    const blob = await canvasToBlob(canvas, mime, q);
    if (blob) {
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= maxBytes) return blob;
    }
    q -= step;
  }
  return best;
}

function canvasWithWhiteBackground(sourceCanvas) {
  const c = document.createElement("canvas");
  c.width = sourceCanvas.width;
  c.height = sourceCanvas.height;
  const x = c.getContext("2d");
  if (!x) return sourceCanvas;
  x.fillStyle = "#ffffff";
  x.fillRect(0, 0, c.width, c.height);
  x.drawImage(sourceCanvas, 0, 0);
  return c;
}

/**
 * Redimensiona (borde máximo) y comprime WebP/JPEG en el cliente.
 * SVG y tipos no raster se devuelven sin cambios.
 *
 * @param {File} file
 * @param {{ maxEdge?: number, quality?: number, maxOutputBytes?: number, skipBelowBytes?: number }} [opts]
 * @returns {Promise<string>} data URL
 */
export async function compressImageFile(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? 1920;
  const startQ = opts.quality ?? 0.82;
  const maxOutputBytes = opts.maxOutputBytes ?? 900 * 1024;
  const skipBelowBytes = opts.skipBelowBytes ?? 120 * 1024;

  if (!file?.type?.startsWith("image/")) {
    return readFileAsDataUrl(file);
  }
  if (file.type === "image/svg+xml") {
    return readFileAsDataUrl(file);
  }
  if (file.size > 0 && file.size < skipBelowBytes) {
    return readFileAsDataUrl(file);
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageFromUrl(objectUrl);
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    if (!w || !h) return readFileAsDataUrl(file);

    const scale = Math.min(1, maxEdge / Math.max(w, h));
    w = Math.max(1, Math.round(w * scale));
    h = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return readFileAsDataUrl(file);
    ctx.drawImage(img, 0, 0, w, h);

    const base = () => canvas;

    const blobW = await blobFromCanvasUnderLimit(base, "image/webp", startQ, 0.48, 0.07, maxOutputBytes);
    if (blobW && blobW.size <= maxOutputBytes) {
      return blobToDataUrl(blobW);
    }

    const jpegCanvas = canvasWithWhiteBackground(canvas);
    const blobJ = await blobFromCanvasUnderLimit(() => jpegCanvas, "image/jpeg", startQ, 0.42, 0.06, maxOutputBytes);
    if (blobJ && blobJ.size <= maxOutputBytes) {
      return blobToDataUrl(blobJ);
    }
    if (blobJ && (!blobW || blobJ.size < blobW.size)) {
      return blobToDataUrl(blobJ);
    }
    if (blobW) {
      return blobToDataUrl(blobW);
    }
    if (blobJ) {
      return blobToDataUrl(blobJ);
    }

    return readFileAsDataUrl(file);
  } catch (_) {
    return readFileAsDataUrl(file);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Para chips: comprime si es imagen; si no, data URL sin tocar.
 */
export async function readChipAttachmentAsDataUrl(file) {
  if (file?.type?.startsWith("image/")) {
    return compressImageFile(file, {
      maxEdge: 1600,
      maxOutputBytes: 700 * 1024,
      skipBelowBytes: 80 * 1024,
      quality: 0.8,
    });
  }
  return readFileAsDataUrl(file);
}
